/** Template editor widget instance registry (mirrors backend/widget_instances.py). */

const RESOURCE_WIDGET_IDS = new Set([
  'form', 'booking', 'quiz', 'reviews', 'blog', 'portfolio', 'pricing', 'team',
  'menu', 'newsletter', 'store', 'featured-products', 'shopping-cart', 'checkout-form',
  'auth-login-signup', 'member-login', 'donation', 'comment-area',
  'order-success', 'navigation', 'store-flows', 'footer', 'hero-gradient',
  'hero-image-bg', 'hero-split', 'hero-banner', 'container', 'icon',
]);

const WIDGET_TYPE_ALIASES = {
  'features-grid': 'features',
  'icon-section': 'features',
};

const SECTION_ID_WIDGET_PREFIXES = [
  ['icon-section-', 'icon-section'],
  ['features-', 'features-grid'],
  ['accordion-', 'accordion'],
  ['separator-', 'separator'],
  ['spacer-', 'spacer'],
  ['cta-', 'cta'],
  ['services-', 'services'],
  ['playlist-', 'video-playlist'],
  ['gallery-', 'gallery'],
  ['slider-', 'slider'],
  ['sale-', 'sale-banner'],
  ['categories-', 'product-categories'],
  ['video-', 'video'],
  ['button-', 'button'],
  ['heading-', 'heading'],
  ['image-', 'image'],
  ['audio-', 'audio'],
];

export function inferWidgetTypeFromSectionHtml(html) {
  if (!html) return null;
  const typeMatch = html.match(/data-widget-type="([^"]+)"/);
  if (typeMatch) return typeMatch[1];
  const idMatch = html.match(/data-section-id="([^"]+)"/);
  const instanceId = idMatch ? idMatch[1] : '';
  for (const [prefix, widgetType] of SECTION_ID_WIDGET_PREFIXES) {
    if (instanceId.startsWith(prefix)) return widgetType;
  }
  return null;
}

export function schemaKeyForWidgetType(widgetType) {
  if (!widgetType) return null;
  const aliased = WIDGET_TYPE_ALIASES[widgetType] || widgetType;
  if (SCHEMA_WIDGET_IDS.has(aliased)) return aliased;
  const underscored = aliased.replace(/-/g, '_');
  if (SCHEMA_WIDGET_IDS.has(underscored)) return underscored;
  return aliased;
}

const SCHEMA_WIDGET_IDS = new Set([
  'heading', 'text', 'button', 'image', 'video', 'audio', 'map', 'countdown',
  'code', 'search', 'progress-bar', 'social-media', 'embed', 'slider', 'gallery', 'video-playlist',
  'accordion', 'separator', 'spacer', 'cta', 'services', 'features', 'features-grid',
  'icon-section', 'sale-banner', 'sale_banner',
  'product-categories', 'product_categories',
]);

export function isConfigWidget(widgetId) {
  if (!widgetId || RESOURCE_WIDGET_IDS.has(widgetId)) return false;
  if (SCHEMA_WIDGET_IDS.has(widgetId)) return true;
  const aliased = WIDGET_TYPE_ALIASES[widgetId];
  if (aliased && SCHEMA_WIDGET_IDS.has(aliased)) return true;
  const alt = widgetId.replace(/-/g, '_');
  return SCHEMA_WIDGET_IDS.has(alt);
}

export function extractInstanceIdFromHtml(html) {
  if (!html) return null;
  const match = html.match(/data-section-id="([^"]+)"/);
  return match ? match[1] : null;
}

export function tagWidgetInstanceHtml(html, widgetId, instanceId) {
  if (!html || !isConfigWidget(widgetId) || !instanceId) return html;
  if (html.includes('data-widget-instance=')) return html;
  return html.replace(
    /(<section[^>]*class="[^"]*widget-section[^"]*"[^>]*)/i,
    `$1 data-widget-instance="${instanceId}" data-widget-type="${widgetId}" data-config-widget="true"`,
  );
}

export function parseWidgetInstanceConfig(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return {};
  }
}

export function normalizeVideoEmbedUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (u.includes('/embed')) return u;
  if (u.includes('youtube.com/watch')) {
    try {
      const vid = new URL(u).searchParams.get('v');
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    } catch {
      /* ignore */
    }
  }
  if (u.includes('youtu.be/')) {
    const parts = u.split('youtu.be/')[1];
    if (parts) return `https://www.youtube.com/embed/${parts.split('?')[0]}`;
  }
  return u;
}

export function extractYoutubeVideoId(url) {
  const u = String(url || '');
  let m = u.match(/embed\/([^?&/]+)/);
  if (m) return m[1];
  m = u.match(/[?&]v=([^&]+)/);
  if (m) return m[1];
  m = u.match(/youtu\.be\/([^?&/]+)/);
  if (m) return m[1];
  return '';
}

function escapeHtmlText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildInstanceLookup(instances) {
  const byId = {};
  (instances || []).forEach((item) => {
    if (item?.instance_id && (item.status || 'active') !== 'removed') {
      byId[item.instance_id] = item;
    }
  });
  return byId;
}

function resolveVideoUrlForSection(section, byId) {
  if (!section) return '';
  const id = section.getAttribute('data-widget-instance') || section.getAttribute('data-section-id');
  const item = id ? byId[id] : null;
  const cfg = item ? parseWidgetInstanceConfig(item.config_json) : {};
  if (cfg.url) return normalizeVideoEmbedUrl(cfg.url);

  const stored = section.getAttribute('data-widget-video-url');
  if (stored) return normalizeVideoEmbedUrl(stored);

  const iframe = section.querySelector('iframe');
  if (iframe?.getAttribute('src')) return normalizeVideoEmbedUrl(iframe.getAttribute('src'));

  const thumb = section.querySelector('[data-editor-video-host] img[src*="img.youtube.com/vi/"]');
  const thumbMatch = thumb?.getAttribute('src')?.match(/\/vi\/([^/]+)\//);
  if (thumbMatch) return `https://www.youtube.com/embed/${thumbMatch[1]}`;

  const urlLine = section.querySelector('[data-editor-video-host] p');
  const lineText = urlLine?.textContent?.trim() || '';
  if (lineText.startsWith('http')) return normalizeVideoEmbedUrl(lineText);

  return '';
}

export function buildVideoEmbedMarkup(embedUrl) {
  const url = normalizeVideoEmbedUrl(embedUrl) || 'https://www.youtube.com/embed/dQw4w9WgXcQ';
  return `<div style="max-width: 900px; margin: 0 auto; position: relative; aspect-ratio: 16/9; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 80px rgba(0,0,0,0.5);">
    <iframe width="100%" height="100%" src="${url}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0;"></iframe>
  </div>`;
}

/** Strip editor-only video preview chrome and restore a real iframe for storage/publish. */
export function repairStoredVideoSections(html, instances = []) {
  if (!html || typeof document === 'undefined') return html;
  if (!html.includes('data-editor-video-host') && !html.includes('data-section-id="video-')) return html;
  try {
    const byId = buildInstanceLookup(instances);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    let changed = false;
    wrapper.querySelectorAll(
      'section[data-widget-type="video"], section[data-section-name="Video"], section[data-section-id^="video-"]',
    ).forEach((section) => {
      const embedUrl = resolveVideoUrlForSection(section, byId);
      const host = section.querySelector('[data-editor-video-host]');
      if (host || !section.querySelector('iframe')) {
        const container = document.createElement('div');
        container.innerHTML = buildVideoEmbedMarkup(embedUrl);
        const replacement = container.firstElementChild;
        if (host) {
          host.replaceWith(replacement);
        } else {
          section.innerHTML = '';
          section.appendChild(replacement);
        }
        changed = true;
      } else if (embedUrl) {
        const iframe = section.querySelector('iframe');
        if (iframe && iframe.getAttribute('src') !== embedUrl) {
          iframe.setAttribute('src', embedUrl);
          changed = true;
        }
      }
      if (embedUrl) section.setAttribute('data-widget-video-url', embedUrl);
      section.querySelectorAll('[data-editor-video-host]').forEach((node) => node.remove());
    });
    return changed ? wrapper.innerHTML : html;
  } catch {
    return html;
  }
}

/**
 * YouTube blocks embeds inside the editor preview iframe (Error 153).
 * Show a thumbnail + play overlay using saved widget_instances config.
 */
export function enhanceEditorVideoPreview(html, instances = []) {
  if (!html || typeof document === 'undefined') return html;
  try {
    const byId = buildInstanceLookup(instances);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    wrapper.querySelectorAll(
      'section[data-widget-type="video"], section[data-section-name="Video"], section[data-section-id^="video-"]',
    ).forEach((section) => {
      const embedUrl = resolveVideoUrlForSection(section, byId);
      if (embedUrl) section.setAttribute('data-widget-video-url', embedUrl);
      const ytId = extractYoutubeVideoId(embedUrl);
      const host = section.querySelector('[data-editor-video-host]')
        || section.querySelector('div[style*="aspect-ratio"]')
        || section.querySelector(':scope > div');
      if (!host) return;

      let inner = '';
      if (ytId) {
        inner = `<div data-editor-video-host="true" style="position:relative;width:100%;aspect-ratio:16/9;min-height:240px;background:#0f172a;border-radius:20px;overflow:hidden;box-shadow:0 25px 80px rgba(0,0,0,0.5);">
  <img src="https://img.youtube.com/vi/${ytId}/hqdefault.jpg" alt="Video preview" style="width:100%;height:100%;object-fit:cover;display:block;" referrerpolicy="no-referrer" />
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(15,23,42,0.45);color:#fff;text-align:center;gap:10px;padding:16px;">
    <div style="width:72px;height:72px;border-radius:50%;background:rgba(255,255,255,0.95);color:#dc2626;display:flex;align-items:center;justify-content:center;font-size:2rem;box-shadow:0 8px 30px rgba(0,0,0,0.45);">▶</div>
    <p style="font-weight:700;font-size:0.95rem;margin:0;">Video preview</p>
    <p style="font-size:0.72rem;opacity:0.9;margin:0;max-width:90%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtmlText(embedUrl)}</p>
    <p style="font-size:0.68rem;opacity:0.75;margin:0;">Plays on your published site — YouTube cannot embed inside the editor preview.</p>
  </div>
</div>`;
      } else if (embedUrl) {
        inner = `<div data-editor-video-host="true" style="position:relative;width:100%;aspect-ratio:16/9;min-height:240px;background:#0f172a;border-radius:20px;overflow:hidden;display:flex;align-items:center;justify-content:center;color:#e2e8f0;text-align:center;padding:24px;">
  <div><p style="font-weight:700;margin:0 0 8px;">Video configured</p><p style="font-size:0.8rem;opacity:0.85;margin:0;word-break:break-all;">${escapeHtmlText(embedUrl)}</p></div>
</div>`;
      } else {
        inner = `<div data-editor-video-host="true" style="position:relative;width:100%;aspect-ratio:16/9;min-height:200px;background:#1e293b;border-radius:20px;display:flex;align-items:center;justify-content:center;color:#94a3b8;text-align:center;padding:24px;border:2px dashed #475569;">
  <p style="margin:0;font-weight:600;">No video URL yet — configure in Widget Dashboard</p>
</div>`;
      }
      host.outerHTML = inner;
    });
    return wrapper.innerHTML;
  } catch {
    return html;
  }
}

function findWidgetContentGrid(section) {
  if (!section) return null;
  return section.querySelector('[data-gallery-grid], [data-widget-content-grid], .operate-resource-grid, [data-feature-grid]')
    || section.querySelector(':scope > div > div:last-child');
}

/** Resolve or create the gallery grid host (supports legacy static-img sections). */
export function ensureGalleryGrid(section) {
  if (!section) return null;
  let grid = section.querySelector('[data-gallery-grid], [data-widget-content-grid]');
  if (grid) return grid;

  const legacyGrid = section.querySelector(
    ':scope > div[style*="grid"], :scope > div > div[style*="grid"], :scope > div[style*="display: grid"], :scope > div[style*="display:grid"]',
  );
  if (legacyGrid) {
    legacyGrid.setAttribute('data-gallery-grid', 'true');
    legacyGrid.setAttribute('data-widget-content-grid', 'true');
    return legacyGrid;
  }

  const wrapper = section.querySelector(':scope > div') || section;
  grid = document.createElement('div');
  grid.setAttribute('data-gallery-grid', 'true');
  grid.setAttribute('data-widget-content-grid', 'true');
  grid.style.display = 'grid';
  grid.style.gap = '15px';
  wrapper.appendChild(grid);
  return grid;
}

export function extractGalleryItemsFromDom(section) {
  if (!section) return [];
  const grid = section.querySelector('[data-gallery-grid], [data-widget-content-grid]')
    || section.querySelector(':scope > div[style*="grid"], :scope > div > div[style*="grid"]');
  const imgs = grid ? grid.querySelectorAll('img') : section.querySelectorAll(':scope img');
  return Array.from(imgs).map((img, i) => ({
    image: img.getAttribute('src') || img.src || '',
    title: img.getAttribute('alt') || `Image ${i + 1}`,
    category: '',
  })).filter((item) => item.image);
}

function buildGalleryTileHtml(item, i) {
  const imgUrl = item.image || item.url || '';
  const title = item.title || '';
  const category = item.category || '';
  return `<div style="position:relative;overflow:hidden;border-radius:12px;aspect-ratio:4/3;cursor:pointer;background:#f0f0f0;" onmouseover="this.querySelector('.overlay').style.opacity=1" onmouseout="this.querySelector('.overlay').style.opacity=0">`
    + `<img src="${escapeHtmlText(imgUrl)}" alt="${escapeHtmlText(title)}" data-gallery-index="${i}" style="width:100%;height:100%;object-fit:cover;transition:transform 0.3s;position:relative;z-index:1;" onmouseover="this.style.transform='scale(1.06)'" onmouseout="this.style.transform='scale(1)'" onerror="this.src='https://picsum.photos/seed/gallery${i}/800/600'">`
    + `<div class="overlay" style="position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.75) 0%,transparent 50%);opacity:0;transition:opacity 0.3s;display:flex;align-items:flex-end;padding:16px;pointer-events:none;z-index:2;">`
    + `<div><h4 style="color:#fff;font-size:16px;font-weight:700;margin-bottom:4px;">${escapeHtmlText(title)}</h4>`
    + (category ? `<p style="color:rgba(255,255,255,0.75);font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:0.05em;margin:0;">${escapeHtmlText(category)}</p>` : '')
    + `</div></div></div>`;
}

export function applyAccordionInstanceConfig(section, cfg) {
  const items = Array.isArray(cfg.items) ? cfg.items : [];
  if (!section || !items.length) return;
  const container = section.querySelector('[data-accordion-items]') || section.querySelector(':scope > div > div');
  if (!container) return;
  container.innerHTML = items.map((item) => (
    `<details style="background:#f8fafc;border-radius:12px;padding:20px;margin-bottom:15px;">`
    + `<summary style="font-weight:600;cursor:pointer;color:#1e293b;">${escapeHtmlText(item.title || 'Question')}</summary>`
    + `<p style="margin-top:15px;color:#64748b;line-height:1.7;">${escapeHtmlText(item.content || '')}</p>`
    + `</details>`
  )).join('');
}

export function applySeparatorInstanceConfig(section, cfg) {
  if (!section) return;
  const hr = section.querySelector('hr');
  if (!hr) return;
  const color = cfg.color || '#e2e8f0';
  const thickness = cfg.thickness || '2px';
  const width = cfg.width || '200px';
  const style = cfg.style || 'solid';
  hr.style.border = 'none';
  hr.style.height = thickness;
  hr.style.maxWidth = width;
  hr.style.margin = '0 auto';
  if (style === 'dashed') {
    hr.style.background = 'transparent';
    hr.style.borderTop = `${thickness} dashed ${color}`;
  } else if (style === 'dotted') {
    hr.style.background = 'transparent';
    hr.style.borderTop = `${thickness} dotted ${color}`;
  } else {
    hr.style.background = `linear-gradient(90deg, transparent, ${color}, transparent)`;
  }
}

export function applySpacerInstanceConfig(section, cfg) {
  if (!section) return;
  const height = cfg.height || '50px';
  section.style.minHeight = height;
  section.style.padding = `${Math.max(parseInt(height, 10) / 2, 20) || 25}px 20px`;
}

export function applyCtaInstanceConfig(section, cfg) {
  if (!section || !cfg) return;
  const heading = section.querySelector('h2');
  const desc = section.querySelector('p');
  const links = section.querySelectorAll('a');
  if (heading && cfg.title) heading.textContent = cfg.title;
  if (desc && cfg.description) desc.textContent = cfg.description;
  if (cfg.bgColor) section.style.background = cfg.bgColor;
  if (cfg.textColor) {
    section.style.color = cfg.textColor;
    if (heading) heading.style.color = cfg.textColor;
    if (desc) desc.style.color = cfg.textColor;
  }
  const primary = links[0];
  if (primary) {
    if (cfg.buttonText) primary.textContent = cfg.buttonText;
    if (cfg.buttonUrl) primary.setAttribute('href', cfg.buttonUrl);
  }
}

export function applyFeaturesInstanceConfig(section, cfg, widgetType = 'features-grid') {
  if (!section || !cfg) return;
  const titleEl = section.querySelector('h2');
  const subtitleEl = section.querySelector('[data-section-subtitle], .section-subtitle');
  if (titleEl && cfg.title) titleEl.textContent = cfg.title;
  if (subtitleEl && cfg.subtitle) subtitleEl.textContent = cfg.subtitle;
  if (cfg.bgColor) section.style.background = cfg.bgColor;
  const items = Array.isArray(cfg.items) ? cfg.items : [];
  const grid = findWidgetContentGrid(section);
  if (!grid || !items.length) return;
  const cols = cfg.columns || (widgetType === 'icon-section' ? '4' : '3');
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  grid.style.gap = '30px';
  grid.innerHTML = items.map((item) => (
    `<div style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);text-align:center;">`
    + `<div style="width:60px;height:60px;background:linear-gradient(135deg,var(--primary-color,#8b5cf6),var(--secondary-color,#10b981));border-radius:15px;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:1.5rem;">${escapeHtmlText(item.icon || '⭐')}</div>`
    + `<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;color:#1e293b;">${escapeHtmlText(item.title || '')}</h3>`
    + `<p style="color:#64748b;font-size:0.9rem;line-height:1.6;">${escapeHtmlText(item.description || '')}</p>`
    + `</div>`
  )).join('');
}

export function applyServicesInstanceConfig(section, cfg) {
  if (!section || !cfg) return;
  const titleEl = section.querySelector('h2');
  const subtitleEl = section.querySelector('[data-section-subtitle], .section-subtitle');
  if (titleEl && cfg.title) titleEl.textContent = cfg.title;
  if (subtitleEl && cfg.subtitle) subtitleEl.textContent = cfg.subtitle;
  const items = Array.isArray(cfg.items) ? cfg.items : [];
  const grid = findWidgetContentGrid(section);
  if (!grid || !items.length) return;
  const cols = cfg.columns || '3';
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  grid.style.gap = '30px';
  grid.innerHTML = items.map((item) => {
    const features = Array.isArray(item.features) ? item.features : [];
    const featureHtml = features.map((f) => (
      `<li style="padding:8px 0;font-size:14px;color:#374151;display:flex;align-items:center;gap:8px;">`
      + `<span style="color:#22c55e;font-weight:bold;">✓</span>${escapeHtmlText(f)}</li>`
    )).join('');
    return `<div style="background:white;border-radius:16px;padding:30px;box-shadow:0 4px 20px rgba(0,0,0,0.08);text-align:left;">`
      + `<div style="font-size:2rem;margin-bottom:16px;">${escapeHtmlText(item.icon || '🔧')}</div>`
      + `<h3 style="font-size:1.2rem;margin-bottom:10px;font-weight:600;color:#1e293b;">${escapeHtmlText(item.title || '')}</h3>`
      + `<p style="color:#64748b;font-size:0.9rem;line-height:1.6;margin-bottom:16px;">${escapeHtmlText(item.description || '')}</p>`
      + (item.price ? `<div style="font-size:1.4rem;font-weight:800;color:#1e293b;margin-bottom:12px;">${escapeHtmlText(item.price)}</div>` : '')
      + (featureHtml ? `<ul style="list-style:none;padding:0;margin:0;border-top:1px solid #f0f0f0;padding-top:12px;">${featureHtml}</ul>` : '')
      + `</div>`;
  }).join('');
}

export function applyGalleryInstanceConfig(section, cfg) {
  if (!section || !cfg) return;
  const titleEl = section.querySelector('[data-gallery-title], h2');
  const subtitleEl = section.querySelector('[data-gallery-subtitle], [data-section-subtitle]');
  if (titleEl && cfg.title) titleEl.textContent = cfg.title;
  if (subtitleEl && cfg.subtitle) {
    subtitleEl.textContent = cfg.subtitle;
    subtitleEl.style.display = cfg.subtitle ? '' : 'none';
  }
  let items = Array.isArray(cfg.items) ? cfg.items : [];
  if (!items.length) items = extractGalleryItemsFromDom(section);
  const grid = ensureGalleryGrid(section);
  if (!grid || !items.length) return;
  const cols = cfg.columns || '3';
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  grid.style.gap = '15px';
  grid.innerHTML = items.map((item, i) => buildGalleryTileHtml(item, i)).join('');
}

export function applyVideoPlaylistInstanceConfig(section, cfg) {
  if (!section || !cfg) return;
  const titleEl = section.querySelector('[data-playlist-title], h2');
  if (titleEl && cfg.title) titleEl.textContent = cfg.title;
  const videos = Array.isArray(cfg.videos) ? cfg.videos : [];
  const grid = section.querySelector('[data-video-playlist-grid], [data-widget-content-grid]') || findWidgetContentGrid(section);
  if (!grid || !videos.length) return;
  const cols = cfg.columns || '3';
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  grid.style.gap = '25px';
  grid.innerHTML = videos.map((video) => {
    const embedUrl = normalizeVideoEmbedUrl(video.url || '');
    const ytId = extractYoutubeVideoId(embedUrl);
    const thumb = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '';
    const watchUrl = ytId ? `https://www.youtube.com/watch?v=${ytId}` : embedUrl;
    return `<div style="background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 15px rgba(0,0,0,0.08);">`
      + `<a href="${escapeHtmlText(watchUrl)}" target="_blank" rel="noopener noreferrer" style="display:block;aspect-ratio:16/9;position:relative;background:#1e293b;text-decoration:none;">`
      + (thumb
        ? `<img src="${escapeHtmlText(thumb)}" alt="${escapeHtmlText(video.title || 'Video')}" style="width:100%;height:100%;object-fit:cover;display:block;" referrerpolicy="no-referrer" />`
        : '')
      + `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.35);"><span style="font-size:3rem;">▶</span></div>`
      + `</a>`
      + `<div style="padding:20px;"><h3 style="font-weight:600;margin:0 0 6px;">${escapeHtmlText(video.title || 'Video')}</h3>`
      + (video.duration ? `<p style="color:#64748b;font-size:0.9rem;margin:0;">${escapeHtmlText(video.duration)}</p>` : '')
      + `</div></div>`;
  }).join('');
}

export function applySliderInstanceConfig(section, cfg) {
  if (!section || !cfg) return;
  const height = cfg.height || '500px';
  section.style.height = height;
  section.style.position = 'relative';
  section.style.overflow = 'hidden';
  const images = Array.isArray(cfg.images) ? cfg.images : [];
  const track = section.querySelector('[data-slider-track]') || section.querySelector(':scope > div');
  if (!track) return;
  track.setAttribute('data-slider-track', 'true');
  track.style.display = 'flex';
  track.style.height = '100%';
  track.style.transition = 'transform 0.5s ease';
  track.style.width = '100%';
  if (images.length) {
    track.innerHTML = images.map((slide, i) => {
      const url = slide.url || slide.image || '';
      const alt = slide.alt || slide.caption || `Slide ${i + 1}`;
      const caption = slide.caption || slide.title || '';
      return `<div style="min-width:100%;position:relative;flex-shrink:0;height:100%;">`
        + `<img src="${escapeHtmlText(url)}" alt="${escapeHtmlText(alt)}" data-slide-index="${i}" style="width:100%;height:100%;object-fit:cover;" />`
        + (caption ? `<div style="position:absolute;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><div style="text-align:center;color:white;padding:40px;"><h2 style="font-size:3rem;font-weight:800;margin-bottom:15px;">${escapeHtmlText(caption)}</h2></div></div>` : '')
        + `</div>`;
    }).join('');
  }
  section.setAttribute('data-slider-autoplay', cfg.autoplay !== false ? 'true' : 'false');
  section.setAttribute('data-slider-interval', String(cfg.interval || '3000'));
  delete section.dataset.sliderInit;
}

/** Initialize carousel behavior for hydrated image sliders (preview + published). */
export function initWidgetSliders(root = typeof document !== 'undefined' ? document : null) {
  if (!root) return;
  root.querySelectorAll('section[data-section-id^="slider-"], section[data-widget-type="slider"]').forEach((section) => {
    if (section.dataset.sliderInit === 'true') return;
    const track = section.querySelector('[data-slider-track]');
    if (!track || track.children.length <= 1) return;
    section.dataset.sliderInit = 'true';
    let index = 0;
    const intervalMs = parseInt(section.getAttribute('data-slider-interval') || '3000', 10);
    const autoplay = section.getAttribute('data-slider-autoplay') !== 'false';

    function goTo(i) {
      const n = track.children.length;
      index = ((i % n) + n) % n;
      track.style.transform = `translateX(-${index * 100}%)`;
      section.querySelectorAll('[data-slider-dot]').forEach((dot, di) => {
        dot.style.background = di === index ? '#fff' : 'rgba(255,255,255,0.5)';
      });
    }

    const dotsHost = section.querySelector('[data-slider-dots]');
    if (dotsHost) {
      dotsHost.innerHTML = Array.from(track.children).map((_, i) => (
        `<button type="button" data-slider-dot="${i}" aria-label="Slide ${i + 1}" style="width:10px;height:10px;border-radius:50%;border:none;cursor:pointer;background:${i === 0 ? '#fff' : 'rgba(255,255,255,0.5)'};"></button>`
      )).join('');
      dotsHost.querySelectorAll('[data-slider-dot]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(parseInt(btn.getAttribute('data-slider-dot'), 10)));
      });
    }

    const prev = section.querySelector('[data-slider-prev]');
    const next = section.querySelector('[data-slider-next]');
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));

    if (autoplay) {
      setInterval(() => goTo(index + 1), intervalMs);
    }
  });
}

/** Apply saved button widget config to a DOM element (preview + storage hydration). */
export function applyButtonInstanceConfig(btn, section, cfg) {
  if (!btn || !cfg) return;
  if (cfg.text) btn.textContent = cfg.text;
  const url = cfg.url || '';
  if (url && btn.tagName === 'A') btn.setAttribute('href', url);
  if (cfg.bgColor) {
    if (cfg.style === 'outline') {
      btn.style.background = 'transparent';
      btn.style.border = `2px solid ${cfg.bgColor}`;
    } else {
      btn.style.background = cfg.bgColor;
    }
  }
  if (cfg.textColor) btn.style.color = cfg.textColor;
  if (cfg.size === 'small') {
    btn.style.padding = '10px 24px';
    btn.style.fontSize = '0.875rem';
  } else if (cfg.size === 'large') {
    btn.style.padding = '20px 48px';
    btn.style.fontSize = '1.125rem';
  }
  if (section && cfg.alignment) section.style.textAlign = cfg.alignment;
}

/** Apply saved widget_instances config directly into preview HTML (sync, before iframe load). */
export function applyWidgetInstancesToHtml(html, instances) {
  if (!html || !Array.isArray(instances) || !instances.length) return html;
  if (typeof document === 'undefined') return html;
  try {
    const repaired = repairStoredVideoSections(html, instances);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = repaired;
    const byId = buildInstanceLookup(instances);
    wrapper.querySelectorAll('section[data-section-id], section[data-widget-instance]').forEach((section) => {
      const id = section.getAttribute('data-widget-instance') || section.getAttribute('data-section-id');
      const item = id ? byId[id] : null;
      if (!item) return;
      const cfg = parseWidgetInstanceConfig(item.config_json);
      const type = (
        item.widget_type
        || section.getAttribute('data-widget-type')
        || inferWidgetTypeFromSectionHtml(section.outerHTML)
        || ''
      ).toLowerCase();
      if (type === 'video' && cfg.url) {
        const embedUrl = normalizeVideoEmbedUrl(cfg.url);
        section.setAttribute('data-widget-video-url', embedUrl);
        let iframe = section.querySelector('iframe');
        if (!iframe) {
          const container = document.createElement('div');
          container.innerHTML = buildVideoEmbedMarkup(embedUrl);
          const block = container.firstElementChild;
          const host = section.querySelector('[data-editor-video-host]') || section.querySelector(':scope > div');
          if (host) host.replaceWith(block);
          else section.appendChild(block);
          iframe = section.querySelector('iframe');
        }
        if (iframe) iframe.setAttribute('src', embedUrl);
        return;
      }
      if (type === 'audio') {
        const src = cfg.src || cfg.url || '';
        const audio = section.querySelector('audio');
        if (audio && src) {
          const source = audio.querySelector('source');
          if (source) source.setAttribute('src', src);
          else audio.setAttribute('src', src);
        }
        const titleEl = section.querySelector('h3');
        if (titleEl && cfg.title) titleEl.textContent = cfg.title;
        return;
      }
      if (type === 'heading') {
        const heading = section.querySelector('h1,h2,h3,h4,h5,h6');
        if (heading && cfg.text) heading.textContent = cfg.text;
        return;
      }
      if (type === 'text') {
        const para = section.querySelector('p');
        if (para && cfg.content) para.textContent = cfg.content;
        return;
      }
      if (type === 'button') {
        const btn = section.querySelector('a,button');
        applyButtonInstanceConfig(btn, section, cfg);
        return;
      }
      if (type === 'accordion') {
        applyAccordionInstanceConfig(section, cfg);
        return;
      }
      if (type === 'separator') {
        applySeparatorInstanceConfig(section, cfg);
        return;
      }
      if (type === 'spacer') {
        applySpacerInstanceConfig(section, cfg);
        return;
      }
      if (type === 'cta') {
        applyCtaInstanceConfig(section, cfg);
        return;
      }
      if (type === 'services') {
        applyServicesInstanceConfig(section, cfg);
        return;
      }
      if (type === 'features-grid' || type === 'icon-section' || type === 'features') {
        applyFeaturesInstanceConfig(section, cfg, type);
        return;
      }
      if (type === 'gallery') {
        applyGalleryInstanceConfig(section, cfg);
        return;
      }
      if (type === 'video-playlist') {
        applyVideoPlaylistInstanceConfig(section, cfg);
        return;
      }
      if (type === 'slider') {
        applySliderInstanceConfig(section, cfg);
        return;
      }
      if (type === 'sale-banner' || type === 'sale_banner') {
        if (cfg.backgroundImage) {
          section.style.backgroundImage = `url('${String(cfg.backgroundImage).replace(/'/g, "\\'")}')`;
        }
        const overlay = section.querySelector('[data-sale-overlay]');
        if (overlay && cfg.overlayColor) overlay.style.background = cfg.overlayColor;
        const badge = section.querySelector('[data-sale-badge]');
        if (badge && cfg.badge) badge.textContent = cfg.badge;
        const saleTitle = section.querySelector('[data-sale-title]');
        if (saleTitle && cfg.title) saleTitle.textContent = cfg.title;
        const saleSubtitle = section.querySelector('[data-sale-subtitle]');
        if (saleSubtitle && cfg.subtitle) saleSubtitle.textContent = cfg.subtitle;
        const saleBtn = section.querySelector('[data-sale-button]');
        if (saleBtn) {
          if (cfg.buttonText) saleBtn.textContent = cfg.buttonText;
          if (cfg.buttonUrl) saleBtn.setAttribute('href', cfg.buttonUrl);
        }
        return;
      }
      if (type === 'product-categories' || type === 'product_categories') {
        const catTitle = section.querySelector('[data-categories-title]');
        const catSub = section.querySelector('[data-categories-subtitle]');
        if (catTitle && cfg.title) catTitle.textContent = cfg.title;
        if (catSub && cfg.subtitle) catSub.textContent = cfg.subtitle;
        const catGrid = section.querySelector('#categories-grid, .categories-grid');
        if (catGrid && cfg.columns) catGrid.style.gridTemplateColumns = `repeat(${cfg.columns}, 1fr)`;
        return;
      }
      if (type === 'image') {
        const img = section.querySelector('img');
        if (img) {
          const htmlSrc = img.getAttribute('src') || '';
          if (cfg.src && htmlSrc && htmlSrc !== cfg.src && !htmlSrc.includes('images.unsplash.com/photo-1557683316')) {
            // Inline editor change wins over stale instance config until synced
            return;
          }
          if (cfg.src) img.setAttribute('src', cfg.src);
          if (cfg.alt) img.setAttribute('alt', cfg.alt);
        }
        return;
      }
    });
    return wrapper.innerHTML;
  } catch {
    return html;
  }
}

/** Loose URL match for gallery/slider image swap in the editor. */
export function mediaUrlMatches(a, b) {
  if (!a || !b) return false;
  const norm = (u) => {
    try {
      return decodeURIComponent(String(u).trim().split('?')[0].replace(/\/$/, ''));
    } catch {
      return String(u).trim().split('?')[0].replace(/\/$/, '');
    }
  };
  const left = norm(a);
  const right = norm(b);
  return left === right || left.endsWith(right) || right.endsWith(left);
}

export function findGalleryItemIndex(items, matchSrc, explicitIndex) {
  if (explicitIndex != null && explicitIndex !== '') {
    const idx = parseInt(String(explicitIndex), 10);
    if (!Number.isNaN(idx) && idx >= 0) return idx;
  }
  const list = Array.isArray(items) ? items : [];
  const hit = list.findIndex((item) => mediaUrlMatches(item?.image || item?.url, matchSrc));
  return hit >= 0 ? hit : 0;
}

export function findSliderImageIndex(images, matchSrc, explicitIndex) {
  if (explicitIndex != null && explicitIndex !== '') {
    const idx = parseInt(String(explicitIndex), 10);
    if (!Number.isNaN(idx) && idx >= 0) return idx;
  }
  const list = Array.isArray(images) ? images : [];
  const hit = list.findIndex((slide) => mediaUrlMatches(slide?.url || slide?.image, matchSrc));
  return hit >= 0 ? hit : 0;
}

export function patchGalleryItemImage(cfg, index, newUrl, bootstrapItems = null) {
  let items = Array.isArray(cfg?.items) ? cfg.items.map((item) => ({ ...item })) : [];
  if (!items.length && Array.isArray(bootstrapItems) && bootstrapItems.length) {
    items = bootstrapItems.map((item) => ({ ...item }));
  }
  if (!items.length) {
    items = [{ image: newUrl, title: '', category: '' }];
    index = 0;
  }
  if (index < 0 || index >= items.length) index = 0;
  items[index] = { ...items[index], image: newUrl };
  return { ...(cfg || {}), items };
}

export function patchSliderSlideImage(cfg, index, newUrl) {
  const images = Array.isArray(cfg?.images) ? cfg.images.map((slide) => ({ ...slide })) : [];
  if (!images.length || index < 0 || index >= images.length) return cfg || {};
  images[index] = { ...images[index], url: newUrl };
  return { ...(cfg || {}), images };
}

export async function fetchWidgetInstances(apiUrl, token, websiteId) {
  if (!websiteId) return [];
  try {
    const response = await fetch(`${apiUrl}/api/sites/${websiteId}/resources/widget_instances?limit=200`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return [];
    const data = await response.json();
    return (data.items || []).filter((item) => (item.status || 'active') !== 'removed');
  } catch {
    return [];
  }
}

export async function registerWidgetInstance(apiUrl, token, websiteId, payload) {
  if (!websiteId || !payload?.instance_id) return null;
  try {
    const response = await fetch(`${apiUrl}/api/websites/${websiteId}/widget-instances/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.error('widget instance register failed', response.status, await response.text());
      return null;
    }
    const data = await response.json();
    return data.item || null;
  } catch (err) {
    console.error('widget instance register error', err);
    return null;
  }
}

export async function syncWidgetInstancesFromPages(apiUrl, token, websiteId) {
  if (!websiteId) return [];
  try {
    const response = await fetch(`${apiUrl}/api/websites/${websiteId}/widget-instances/sync-from-pages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.items || [];
  } catch {
    return [];
  }
}

export async function deactivateWidgetInstance(apiUrl, token, websiteId, instanceId) {
  if (!websiteId || !instanceId) return false;
  try {
    const response = await fetch(`${apiUrl}/api/websites/${websiteId}/widget-instances/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ instance_id: instanceId }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function updateWidgetInstanceConfig(apiUrl, token, websiteId, rowId, config) {
  if (!websiteId || !rowId) return null;
  try {
    const response = await fetch(`${apiUrl}/api/sites/${websiteId}/resources/widget_instances/${rowId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ config_json: JSON.stringify(config || {}) }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.item || data;
  } catch {
    return null;
  }
}

/** Merge config fields for a widget instance matched by instance_id (section id). */
export async function patchWidgetInstanceConfig(apiUrl, token, websiteId, instanceId, patch, knownInstances = null, registerPayload = null) {
  if (!websiteId || !instanceId || !patch) return null;
  let instances = knownInstances || await fetchWidgetInstances(apiUrl, token, websiteId);
  let row = (instances || []).find((item) => item.instance_id === instanceId);
  if (!row?.id && registerPayload) {
    const registered = await registerWidgetInstance(apiUrl, token, websiteId, registerPayload);
    if (registered) {
      row = registered;
      instances = [...(instances || []).filter((item) => item.instance_id !== instanceId), registered];
    }
  }
  if (!row?.id) return null;
  const nextConfig = { ...parseWidgetInstanceConfig(row.config_json), ...patch };
  return updateWidgetInstanceConfig(apiUrl, token, websiteId, row.id, nextConfig);
}

export function widgetDashboardUrl(websiteId, instanceId) {
  const params = new URLSearchParams({ view: 'widgets', section: 'widgets' });
  if (instanceId) params.set('instance', instanceId);
  return `/sites/${websiteId}/operate?${params.toString()}`;
}

export function widgetInstanceFromSection(section, currentPageSlug) {
  const html = section?.html || '';
  const instanceId = extractInstanceIdFromHtml(html);
  const inferredType = inferWidgetTypeFromSectionHtml(html);
  const typeMatch = html.match(/data-widget-type="([^"]+)"/);
  const widgetType = typeMatch ? typeMatch[1] : inferredType;
  if (!instanceId || !widgetType) {
    const sectionId = instanceId || '';
    const inferred = inferredType || (
      sectionId.startsWith('video-') ? 'video'
      : sectionId.startsWith('button-') ? 'button'
      : sectionId.startsWith('heading-') ? 'heading'
      : sectionId.startsWith('image-') ? 'image'
      : sectionId.startsWith('audio-') ? 'audio'
      : sectionId.startsWith('gallery-') ? 'gallery'
      : sectionId.startsWith('slider-') ? 'slider'
      : sectionId.startsWith('playlist-') ? 'video-playlist'
      : null
    );
    if (!instanceId || !inferred || !isConfigWidget(inferred)) return null;
    return {
      instance_id: instanceId,
      widget_type: inferred,
      widget_name: section?.name || inferred,
      page_slug: currentPageSlug || '',
    };
  }
  if (!isConfigWidget(widgetType)) return null;
  return {
    instance_id: instanceId,
    widget_type: widgetType,
    widget_name: section?.name || widgetType,
    page_slug: currentPageSlug || '',
  };
}
